import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCodeBranch, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import NavBar from "../components/common/navBar";
import Seo from "../components/common/seo";

import INFO from "../data/user";
import { getProjectBySlug, getProjectPath } from "../data/projectUtils";
import { buildProjectSchema } from "../data/schema";

import "./styles/projectCaseStudy.css";

const ProjectCaseStudy = () => {
	const { slug } = useParams();
	const project = getProjectBySlug(INFO.projects, slug);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [slug]);

	if (!project) {
		return (
			<React.Fragment>
				<Seo
					title={`Project Not Found | ${INFO.main.name}`}
					description="The requested project case study was not found."
					path={`/projects/${slug}`}
					noindex
				/>
				<div className="page-content">
					<NavBar active="projects" />
					<div className="content-wrapper">
						<div className="project-case-logo-container">
							<div className="project-case-logo">
								<Logo width={46} />
							</div>
						</div>
						<main className="project-case-container">
							<h1 className="title project-case-title">Project not found</h1>
							<p className="subtitle project-case-subtitle">
								This case study is not available.
							</p>
							<Link className="project-case-back-link" to="/projects">
								View all projects
							</Link>
						</main>
						<div className="page-footer">
							<Footer />
						</div>
					</div>
				</div>
			</React.Fragment>
		);
	}

	const projectPath = getProjectPath(project);
	const title = `${project.title} Case Study | Sharjeel Faiq`;
	const description = `${project.description} ${project.outcome}`;

	return (
		<React.Fragment>
			<Seo
				title={title}
				description={description}
				keywords={[project.title, ...project.stack, "Sharjeel Faiq", "Full Stack Developer Pakistan"]}
				path={projectPath}
				schema={buildProjectSchema(project)}
				type="article"
			/>

			<div className="page-content">
				<NavBar active="projects" />
				<div className="content-wrapper">
					<div className="project-case-logo-container">
						<div className="project-case-logo">
							<Logo width={46} />
						</div>
					</div>
					<main className="project-case-container">
						<Link className="project-case-back-link" to="/projects">
							All projects
						</Link>
						<div className="project-case-hero">
							<div>
								<div className="project-case-kicker">Project Case Study</div>
								<h1 className="title project-case-title">{project.title}</h1>
								<p className="subtitle project-case-subtitle">
									{project.description}
								</p>
							</div>
							<img
								src={project.logo}
								alt={`${project.title} technology logo`}
								className="project-case-image"
								width="64"
								height="64"
								decoding="async"
								loading="eager"
							/>
						</div>

						<section className="project-case-section">
							<h2>Problem</h2>
							<p>
								{project.title} represents the kind of full-stack problem where
								product clarity, backend reliability, and a usable interface need
								to work together. The core requirement was to turn a technical
								workflow into a dependable web experience.
							</p>
						</section>

						<section className="project-case-section">
							<h2>Solution</h2>
							<p>{project.description}</p>
							<div className="project-case-stack">
								{project.stack.map((item) => (
									<span className="project-case-stack-item" key={item}>
										{item}
									</span>
								))}
							</div>
						</section>

						<section className="project-case-section">
							<h2>Result</h2>
							<p>{project.outcome}</p>
						</section>

						<div className="project-case-actions">
							{project.github && (
								<a
									className="project-case-action"
									href={project.github}
									target="_blank"
									rel="noopener noreferrer"
								>
									<FontAwesomeIcon icon={faCodeBranch} />
									<span>GitHub</span>
								</a>
							)}
							{project.live && (
								<a
									className="project-case-action"
									href={project.live}
									target="_blank"
									rel="noopener noreferrer"
								>
									<FontAwesomeIcon icon={faExternalLinkAlt} />
									<span>Live project</span>
								</a>
							)}
							<Link className="project-case-action" to="/contact">
								Contact Sharjeel
							</Link>
						</div>
					</main>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default ProjectCaseStudy;
